import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Alert, NativeSelect, NumberInput, Stack, Text, Textarea } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { ClearableYesNoRadioField } from "@/components/ClearableYesNoRadioField";
import { CERVICAL_EXAM_DATE_TIME_LINK_ID } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/constants/cervical-exam-link-ids";
import type { CervicalExamFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/types/cervical-exam-form";
import { valueSetContainsToNativeSelectOptions } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

const ACTIVE_LABOR_NOTICE =
  "Selecting yes for 'Active Labor' does not change the current labor stage. To change the current labor stage, use the labor stage controls on the flowsheet.";

interface CervicalExamFormFieldsProps {
  form: UseFormReturnType<CervicalExamFlowsheetFormValues>;
  clientId: string;
  mode?: "quick-add" | "edit";
  isDrawerOpened?: boolean;
}

export function CervicalExamFormFields({
  form,
  clientId,
  mode = "quick-add",
  isDrawerOpened = true,
}: CervicalExamFormFieldsProps) {
  const medplum = useMedplum();
  const [consistencyOptions, setConsistencyOptions] = useState<ValueSetExpansionContains[]>([]);
  const [locationOptions, setLocationOptions] = useState<ValueSetExpansionContains[]>([]);
  const [fetalPresentationOptions, setFetalPresentationOptions] = useState<
    ValueSetExpansionContains[]
  >([]);
  const consistencyInputProps = form.getInputProps("consistency");
  const locationOfCervixInputProps = form.getInputProps("locationOfCervix");
  const fetalPresentationInputProps = form.getInputProps("fetalPresentation");

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadValueSetOptions = async () => {
      try {
        const [consistencyValueSet, locationValueSet, fetalPresentationValueSet] =
          await Promise.all([
            getValueSetOptions(medplum, QuiltedHealthValueSets.CervixConsistency),
            getValueSetOptions(medplum, QuiltedHealthValueSets.CervixLocation),
            getValueSetOptions(medplum, QuiltedHealthValueSets.FetalPresentation),
          ]);

        if (isCancelled) {
          return;
        }

        setConsistencyOptions(consistencyValueSet ?? []);
        setLocationOptions(locationValueSet ?? []);
        setFetalPresentationOptions(fetalPresentationValueSet ?? []);
      } catch (error) {
        console.error("Error loading cervical exam value sets:", error);
        if (!isCancelled) {
          toast({
            type: "error",
            title: "Failed to load form options",
            message:
              "Consistency, location, and fetal presentation options could not be loaded. Try closing and reopening the drawer.",
            autoClose: false,
          });
        }
      }
    };

    void loadValueSetOptions();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName="examDate"
        timeFieldName="examTime"
        autoPopulateDateTime={mode === "quick-add"}
        dateTimeGroupLinkId={CERVICAL_EXAM_DATE_TIME_LINK_ID}
      />

      <NumberInput
        min={0}
        label="Dilation"
        allowNegative={false}
        clampBehavior="strict"
        rightSectionWidth={48}
        data-path="cervixDilation"
        rightSection={
          <Text pr="xs" size="sm">
            cm
          </Text>
        }
        {...form.getInputProps("dilation")}
        data-testid={questionnaireFieldTestId("cervixDilation")}
      />

      <NumberInput
        min={0}
        label="Effacement"
        allowNegative={false}
        clampBehavior="strict"
        rightSectionWidth={48}
        data-path="cervixEffacement"
        rightSection={
          <Text pr="xs" size="sm">
            %
          </Text>
        }
        {...form.getInputProps("effacement")}
        data-testid={questionnaireFieldTestId("cervixEffacement")}
      />

      <NumberInput
        max={4}
        min={-4}
        label="Station"
        decimalScale={0}
        allowDecimal={false}
        clampBehavior="strict"
        data-path="cervixStation"
        placeholder="Select between range of -4 to +4"
        {...form.getInputProps("station")}
        data-testid={questionnaireFieldTestId("cervixStation")}
      />

      <NativeSelect
        label="Consistency"
        data-path="cervixConsistency"
        data={valueSetContainsToNativeSelectOptions(consistencyOptions)}
        {...consistencyInputProps}
        value={consistencyInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("cervixConsistency")}
      />

      <NativeSelect
        label="Location of Cervix"
        data-path="cervixLocationOfCervix"
        data={valueSetContainsToNativeSelectOptions(locationOptions)}
        {...locationOfCervixInputProps}
        value={locationOfCervixInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("cervixLocationOfCervix")}
      />

      <NativeSelect
        label="Fetal Presentation"
        data-path="cervixFetalPresentation"
        data={valueSetContainsToNativeSelectOptions(fetalPresentationOptions)}
        {...fetalPresentationInputProps}
        value={fetalPresentationInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("cervixFetalPresentation")}
      />

      <Alert color="blue" title="Active Labor does not change labor stage">
        {ACTIVE_LABOR_NOTICE}
      </Alert>

      <ClearableYesNoRadioField
        form={form}
        name="activeLabor"
        label="Active Labor"
        dataPath="cervixActiveLabor"
        dataTestId={questionnaireFieldTestId("cervixActiveLabor")}
      />

      <Textarea
        label="Notes"
        data-path="cervixNotes"
        {...form.getInputProps("notes")}
        data-testid={questionnaireFieldTestId("cervixNotes")}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="signatureInitials"
        autoPopulateSignature={mode === "quick-add"}
      />
    </Stack>
  );
}
